import { useEffect, useState } from 'react';
import { Alert, Button, Form, Input, Modal, Popconfirm, Select, Skeleton, Table, message } from 'antd';
import { CheckOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  confirmClientSideContacts,
  getClientSideContacts,
  updateClientSideContacts,
} from '@/services/teamAssignments.service';
import { ContactHistory, ReviewTag, SectionTags } from './contactReviewParts';

const LANGUAGES = ['sr', 'en', 'ru', 'zh', 'pl', 'sq'];
const errorText = (e) => e?.response?.data?.message || e.message;

const toTokens = (sections) => (sections || []).map((s) => `${s.section}:${s.delivery || 'TO'}`);
const fromTokens = (tokens) => {
  const map = new Map();
  (tokens || []).forEach((token) => {
    const [section, delivery] = token.split(':');
    map.set(section, { section, delivery });
  });
  return [...map.values()];
};

/** Adds or changes one contact. */
function ContactForm({ open, contact, sections, onCancel, onSave, saving }) {
  const { t } = useTranslation(['teamContacts', 'teamAssignments']);
  const [form] = Form.useForm();
  useEffect(() => {
    if (open) form.setFieldsValue({ language: 'sr', ...contact, sections: toTokens(contact?.sections) });
  }, [open, contact, form]);
  const options = sections.map((s) => ({
    label: t(`sections.${s}`),
    options: [
      { value: `${s}:TO`, label: `${t(`sections.${s}`)} · ${t('delivery.TO', { ns: 'teamAssignments' })}` },
      { value: `${s}:CC`, label: `${t(`sections.${s}`)} · ${t('delivery.CC', { ns: 'teamAssignments' })}` },
    ],
  }));
  return (
    <Modal
      open={open}
      width={640}
      title={contact?._id ? t('client.editContact') : t('client.addContact')}
      onCancel={onCancel}
      onOk={() => form.submit()}
      okText={t('actions.save', { ns: 'teamAssignments' })}
      cancelText={t('actions.cancel', { ns: 'teamAssignments' })}
      confirmLoading={saving}
      okButtonProps={{ shape: 'round' }}
      cancelButtonProps={{ shape: 'round' }}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => onSave({ ...contact, ...values, sections: fromTokens(values.sections) })}
      >
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="name" label={t('contacts.name', { ns: 'teamAssignments' })}>
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item
            name="email"
            label={t('contacts.email', { ns: 'teamAssignments' })}
            rules={[
              { required: true, message: t('client.emailRequired') },
              { type: 'email', message: t('contacts.invalidEmail', { ns: 'teamAssignments' }) },
            ]}
          >
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="phone" label={t('contacts.phone', { ns: 'teamAssignments' })}>
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item name="position" label={t('contacts.position', { ns: 'teamAssignments' })}>
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="language" label={t('contacts.language', { ns: 'teamAssignments' })}>
            <Select options={LANGUAGES.map((l) => ({ value: l, label: l.toUpperCase() }))} />
          </Form.Item>
        </div>
        <Form.Item
          name="sections"
          label={t('client.receives')}
          extra={t('client.receivesHint')}
          rules={[{ required: true, message: t('client.sectionRequired') }]}
        >
          <Select mode="multiple" options={options} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

/**
 * A client's own view of the company's contacts: who gets what from HLB. They
 * change the list and confirm it, and see who did what and when. Nothing here
 * shows the team's internal fields; the server does not send them.
 *
 * @param {Object} props
 * @param {string} props.companyId
 */
export default function ClientSideContactsPanel({ companyId }) {
  const { t } = useTranslation(['teamContacts', 'teamAssignments']);
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ['client-side-contacts', companyId],
    queryFn: () => getClientSideContacts(companyId),
    enabled: !!companyId,
    retry: false,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['client-side-contacts', companyId] });

  const save = useMutation({
    mutationFn: (contacts) => updateClientSideContacts(companyId, contacts, data.review.revision),
    onSuccess: () => {
      message.success(t('client.saved'));
      setEditing(null);
      refresh();
    },
    onError: (e) => {
      message.error(errorText(e));
      refresh();
    },
  });
  const confirm = useMutation({
    mutationFn: () => confirmClientSideContacts(companyId, data.review.revision),
    onSuccess: () => {
      message.success(t('client.confirmedMessage'));
      refresh();
    },
    onError: (e) => {
      message.error(errorText(e));
      refresh();
    },
  });

  if (isLoading) return <Skeleton active />;
  if (error) return <Alert type="error" showIcon message={errorText(error)} />;
  if (!data) return null;

  const { contacts, review, sections, history, withoutRecipient } = data;
  const saveOne = (contact) =>
    save.mutate(
      contact._id ? contacts.map((c) => (c._id === contact._id ? contact : c)) : [...contacts, contact]
    );

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-4">
      <Alert type="info" showIcon message={t('client.intro')} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <ReviewTag status={review.status} by={review.confirmedBy} at={review.confirmedAt} />
        <div className="flex flex-wrap gap-2">
          <Button shape="round" icon={<PlusOutlined />} onClick={() => setEditing({ sections: [] })}>
            {t('client.addContact')}
          </Button>
          <Button
            type="primary"
            shape="round"
            icon={<CheckOutlined />}
            disabled={!contacts.length || review.status === 'CONFIRMED'}
            loading={confirm.isPending}
            onClick={() => confirm.mutate()}
          >
            {t('client.confirm')}
          </Button>
        </div>
      </div>
      {review.status === 'NEEDS_RECONFIRMATION' && <Alert type="warning" showIcon message={t('client.needsAgain')} />}
      {contacts.length > 0 && withoutRecipient?.some((s) => s !== 'INVOICES') && (
        <Alert
          type="warning"
          showIcon
          message={t('client.withoutRecipient', {
            list: withoutRecipient
              .filter((s) => s !== 'INVOICES')
              .map((s) => t(`sections.${s}`))
              .join(', '),
          })}
        />
      )}
      <Table
        size="small"
        rowKey="_id"
        pagination={false}
        tableLayout="fixed"
        dataSource={contacts}
        locale={{ emptyText: t('client.none') }}
        columns={[
          {
            title: t('contacts.name', { ns: 'teamAssignments' }),
            dataIndex: 'name',
            width: '24%',
            render: (v, r) => (
              <div className="whitespace-normal">
                {v || '—'}
                {r.position ? <span className="opacity-60"> · {r.position}</span> : null}
              </div>
            ),
          },
          {
            title: t('contacts.email', { ns: 'teamAssignments' }),
            dataIndex: 'email',
            width: '30%',
            render: (v) => <div className="whitespace-normal [overflow-wrap:anywhere]">{v}</div>,
          },
          {
            title: t('client.receives'),
            dataIndex: 'sections',
            render: (s) => (
              <div className="whitespace-normal">
                <SectionTags sections={s} />
              </div>
            ),
          },
          {
            title: '',
            key: 'actions',
            width: 96,
            render: (_, row) => (
              <div className="flex gap-1">
                <Button
                  size="small"
                  shape="circle"
                  icon={<EditOutlined />}
                  aria-label={t('client.editContact')}
                  onClick={() => setEditing(row)}
                />
                <Popconfirm
                  title={t('client.removeConfirm', { email: row.email })}
                  okText={t('actions.remove', { ns: 'teamAssignments' })}
                  cancelText={t('actions.cancel', { ns: 'teamAssignments' })}
                  okButtonProps={{ shape: 'round', danger: true }}
                  cancelButtonProps={{ shape: 'round' }}
                  onConfirm={() => save.mutate(contacts.filter((c) => c._id !== row._id))}
                >
                  <Button size="small" shape="circle" danger icon={<DeleteOutlined />} aria-label={t('actions.remove', { ns: 'teamAssignments' })} />
                </Popconfirm>
              </div>
            ),
          },
        ]}
      />
      <div>
        <h4 className="mb-2 font-semibold">{t('history.title')}</h4>
        <ContactHistory entries={history} />
      </div>
      <ContactForm
        open={!!editing}
        contact={editing}
        sections={sections}
        saving={save.isPending}
        onCancel={() => setEditing(null)}
        onSave={saveOne}
      />
    </div>
  );
}
